import { Tab, Tabs } from '@mui/material';
import MuiButton from '@mui/material/Button';
import MuiIconButton from '@mui/material/IconButton';
import { alpha, keyframes, styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import {
  getZoomAdjustedThemeSpacingPx,
  getZoomAdjustedThemeTypographyPx,
} from 'components/ui/utilities/theme';
import { getZoomAdjustedCssValue, getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { PRISM_BREAKPOINTS, RED } from 'theme';
import type {
  SecondaryNavigationActionButtonVariant,
  SecondaryNavigationIconButtonVariant,
  SecondaryNavigationInfoLabelTone,
  SecondaryNavigationSelectorVariant,
  SecondaryNavigationStatusBadgeTone,
} from './SecondaryNavigation.types';

interface ZoomStyleProps {
  zoomLevel: number;
}

const SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX = PRISM_BREAKPOINTS.tablet1024;
const SECONDARY_NAVIGATION_HORIZONTAL_PADDING_PX = 24;
const getSecondaryNavigationTitleMaxWidth = (zoomLevel: number) =>
  `min(${getZoomAdjustedPx(320, zoomLevel)}, ${getZoomAdjustedCssValue('35vw', zoomLevel)})`;

const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';
const shouldForwardActionButtonProp = (prop: PropertyKey) =>
  prop !== 'buttonVariant' && prop !== 'buttonState';
const shouldForwardIconButtonProp = (prop: PropertyKey) => prop !== 'isActive' && prop !== 'iconButtonVariant';
const shouldForwardSelectorProp = (prop: PropertyKey) => prop !== 'selectorVariant';
const shouldForwardVisibleBadgeProp = (prop: PropertyKey) => prop !== 'isVisible';

interface ActionButtonStyleProps {
  buttonVariant: SecondaryNavigationActionButtonVariant;
  buttonState?: SecondaryNavigationActionButtonState;
}

interface ActionButtonContentItemStyleProps {
  contentPosition: SecondaryNavigationActionContentPosition;
}

interface IconButtonStyleProps {
  iconButtonVariant: SecondaryNavigationIconButtonVariant;
  isActive?: boolean;
}

interface InfoLabelValueStyleProps {
  valueTone: SecondaryNavigationInfoLabelTone;
}

const shouldForwardInfoLabelValueProp = (prop: PropertyKey) => prop !== 'valueTone';
const shouldForwardStatusBadgeProp = (prop: PropertyKey) => prop !== 'tone';
const shouldForwardStatusBadgeTextProp = (prop: PropertyKey) => prop !== 'textState';

interface StatusBadgeStyleProps {
  tone: SecondaryNavigationStatusBadgeTone;
}

interface StatusBadgeTextStyleProps {
  textState: 'entering' | 'exiting' | 'idle';
}

interface VisibleBadgeStyleProps {
  isVisible: boolean;
}

interface SelectorStyleProps {
  selectorVariant: SecondaryNavigationSelectorVariant;
}

type SecondaryNavigationActionButtonState = 'idle' | 'saving' | 'saved' | 'error';
type SecondaryNavigationActionContentPosition = 'active' | 'next' | 'previous';

const secondaryNavigationStatusBadgeTextEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(1px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const secondaryNavigationStatusBadgeTextExit = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-1px);
  }
`;

const getSecondaryNavigationStatusBadgeTextAnimation = (
  textState: StatusBadgeTextStyleProps['textState'],
  theme: Theme
) => {
  if (textState === 'entering') {
    return `${secondaryNavigationStatusBadgeTextEnter} 120ms ${theme.transitions.easing.easeOut}`;
  }

  if (textState === 'exiting') {
    return `${secondaryNavigationStatusBadgeTextExit} 80ms ${theme.transitions.easing.easeIn}`;
  }

  return 'none';
};

const secondaryNavigationSavePulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 14px 0 var(--secondary-navigation-save-glow-color);
  }
  50% {
    box-shadow: 0 0 24px 2px var(--secondary-navigation-save-glow-color);
  }
`;

const getSecondaryNavigationDisabledColors = (
  theme: Theme,
  colors: Theme['palette']['navigation']['secondary']['actionButton'][SecondaryNavigationActionButtonVariant],
  isActionState: boolean,
  isErrorState: boolean
) => {
  if (isErrorState) {
    return {
      backgroundColor: RED[600],
      borderColor: RED[600],
      color: theme.palette.common.white,
    };
  }

  if (isActionState) {
    return {
      backgroundColor: colors.background,
      borderColor: colors.border,
      color: colors.text,
    };
  }

  return {
    backgroundColor: colors.background,
    borderColor: colors.border,
    color: colors.text,
  };
};
const secondaryNavigationSavedCheckDraw = keyframes`
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

export const SecondaryNavigationRoot = styled('nav', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme, zoomLevel }) => ({
  '--secondary-navigation-control-height': getZoomAdjustedPx(24, zoomLevel),
  '--secondary-navigation-control-padding-x': getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  '--secondary-navigation-control-padding-y': getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  '--secondary-navigation-control-gap': getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  '--secondary-navigation-control-radius': getZoomAdjustedPx(theme.shape.radius.xs, zoomLevel),
  '--secondary-navigation-border-width': getZoomAdjustedPx(1, zoomLevel),
  '--secondary-navigation-focus-outline-width': getZoomAdjustedPx(2, zoomLevel),
  '--secondary-navigation-font-size': getZoomAdjustedThemeTypographyPx(theme, 'caption', 'fontSize', zoomLevel),
  '--secondary-navigation-filter-selector-max-width': getZoomAdjustedPx(172, zoomLevel),
  '--secondary-navigation-line-height': getZoomAdjustedThemeTypographyPx(theme, 'caption', 'lineHeight', zoomLevel),
  '--secondary-navigation-action-icon-size': getZoomAdjustedPx(16, zoomLevel),
  '--secondary-navigation-contained-icon-button-size': getZoomAdjustedPx(20, zoomLevel),
  '--secondary-navigation-plain-icon-button-size': getZoomAdjustedPx(24, zoomLevel),
  '--secondary-navigation-status-badge-font-size': getZoomAdjustedPx(10, zoomLevel),
  '--secondary-navigation-status-badge-height': getZoomAdjustedPx(18, zoomLevel),
  '--secondary-navigation-status-badge-line-height': getZoomAdjustedPx(14, zoomLevel),
  '--secondary-navigation-ready-for-audit-badge-max-width': getZoomAdjustedPx(88, zoomLevel),
  '--secondary-navigation-scalar-owner-icon-height': getZoomAdjustedPx(15, zoomLevel),
  '--secondary-navigation-scalar-owner-icon-width': getZoomAdjustedPx(14, zoomLevel),
  '--secondary-navigation-status-gap': getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  '--secondary-navigation-status-badge-padding-x': getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  '--secondary-navigation-status-badge-padding-y': getZoomAdjustedThemeSpacingPx(theme, 'xxs', zoomLevel),
  '--secondary-navigation-tab-height': getZoomAdjustedPx(32, zoomLevel),
  '--secondary-navigation-padding-x': getZoomAdjustedPx(SECONDARY_NAVIGATION_HORIZONTAL_PADDING_PX, zoomLevel),
  alignItems: 'center',
  backgroundColor: theme.palette.navigation.secondary.bar.background,
  boxSizing: 'border-box',
  display: 'flex',
  flexShrink: 0,
  height: getZoomAdjustedPx(32, zoomLevel),
  justifyContent: 'space-between',
  minWidth: 0,
  padding: '0 var(--secondary-navigation-padding-x)',
  width: '100%',
  [`@media (max-width: ${SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    alignItems: 'stretch',
    flexDirection: 'column',
    height: 'auto',
    minHeight: getZoomAdjustedPx(32, zoomLevel),
    paddingBottom: getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
    paddingTop: getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
    rowGap: getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  },
}));

export const SecondaryNavigationContent = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme, zoomLevel }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: '1 1 auto',
  gap: getZoomAdjustedThemeSpacingPx(theme, 's', zoomLevel),
  minWidth: 0,
  overflowX: 'hidden',
  overflowY: 'hidden',
  [`@media (max-width: ${SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    width: '100%',
  },
}));

export const SecondaryNavigationTitleGroup = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ zoomLevel }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  flex: '0 1 auto',
  minWidth: 0,
  '--secondary-navigation-title-leading-gap': getZoomAdjustedPx(4, zoomLevel),
}));

export const SecondaryNavigationTitle = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme, zoomLevel }) => {
  const titleMaxWidth = getSecondaryNavigationTitleMaxWidth(zoomLevel);

  return {
    color: theme.palette.navigation.secondary.title.text,
    display: 'block',
    flex: '0 1 auto',
    fontSize: getZoomAdjustedPx(16, zoomLevel),
    fontWeight: theme.typography.fontWeightMedium,
    height: getZoomAdjustedPx(24, zoomLevel),
    lineHeight: getZoomAdjustedPx(24, zoomLevel),
    letterSpacing: 0,
    maxWidth: titleMaxWidth,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [`@media (max-width: ${SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
      maxWidth: '100%',
    },
  };
});

export const SecondaryNavigationStatus = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ zoomLevel }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  minHeight: getZoomAdjustedPx(18, zoomLevel),
  '& > * + *': {
    marginLeft: 'var(--secondary-navigation-status-gap, 4px)',
  },
  '& > [data-secondary-navigation-ready-for-audit-badge="true"][aria-hidden="true"]': {
    marginLeft: 0,
  },
}));

export const SecondaryNavigationStatusBadgeRoot = styled('span', {
  shouldForwardProp: shouldForwardStatusBadgeProp,
})<StatusBadgeStyleProps>(({ theme, tone }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.navigation.secondary.statusBadge[tone].background,
  borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
  boxSizing: 'border-box',
  color: theme.palette.navigation.secondary.statusBadge[tone].text,
  display: 'inline-flex',
  fontSize: 'var(--secondary-navigation-status-badge-font-size, 10px)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--secondary-navigation-status-badge-padding-x, 4px)',
  height: 'var(--secondary-navigation-status-badge-height, 18px)',
  justifyContent: 'center',
  letterSpacing: 0,
  lineHeight: 'var(--secondary-navigation-status-badge-line-height, 14px)',
  padding:
    'var(--secondary-navigation-status-badge-padding-y, 2px) var(--secondary-navigation-status-badge-padding-x, 4px)',
  textAlign: 'center',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: 180,
    easing: theme.transitions.easing.easeOut,
  }),
  whiteSpace: 'nowrap',
  willChange: 'background-color, color',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const SecondaryNavigationStatusBadgeText = styled('span', {
  shouldForwardProp: shouldForwardStatusBadgeTextProp,
})<StatusBadgeTextStyleProps>(({ theme, textState }) => ({
  animation: getSecondaryNavigationStatusBadgeTextAnimation(textState, theme),
  display: 'inline-block',
  whiteSpace: 'nowrap',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

export const SecondaryNavigationReadyForAuditBadgeRoot = styled('span', {
  shouldForwardProp: shouldForwardVisibleBadgeProp,
})<VisibleBadgeStyleProps>(({ theme, isVisible }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  borderColor: alpha(theme.palette.primary.main, 0.28),
  borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
  borderStyle: 'solid',
  borderWidth: isVisible ? theme.spacing(0.125) : 0,
  boxSizing: 'border-box',
  color: theme.palette.primary.main,
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 'var(--secondary-navigation-status-badge-font-size, 10px)',
  fontWeight: theme.typography.fontWeightMedium,
  height: 'var(--secondary-navigation-status-badge-height, 18px)',
  justifyContent: 'center',
  letterSpacing: 0,
  lineHeight: 'var(--secondary-navigation-status-badge-line-height, 14px)',
  maxWidth: isVisible ? 'var(--secondary-navigation-ready-for-audit-badge-max-width, 88px)' : 0,
  opacity: isVisible ? 1 : 0,
  overflow: 'hidden',
  padding:
    `var(--secondary-navigation-status-badge-padding-y, 2px) ${
      isVisible ? 'var(--secondary-navigation-status-badge-padding-x, 4px)' : '0'
    }`,
  pointerEvents: isVisible ? 'auto' : 'none',
  textAlign: 'center',
  transform: isVisible ? 'scale(1)' : 'scale(0.96)',
  transition: theme.transitions.create(
    ['border-width', 'max-width', 'opacity', 'padding', 'transform'],
    {
      duration: 180,
      easing: theme.transitions.easing.easeOut,
    }
  ),
  whiteSpace: 'nowrap',
  willChange: 'max-width, opacity, transform',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const SecondaryNavigationScalarOwnerBadgeRoot = styled('span', {
  shouldForwardProp: shouldForwardVisibleBadgeProp,
})<VisibleBadgeStyleProps>(({ theme, isVisible }) => ({
  alignItems: 'center',
  boxSizing: 'border-box',
  display: 'inline-flex',
  flexShrink: 0,
  height: 'var(--secondary-navigation-scalar-owner-icon-height, 15px)',
  justifyContent: 'center',
  marginRight: isVisible ? 'var(--secondary-navigation-title-leading-gap, 4px)' : 0,
  opacity: isVisible ? 1 : 0,
  overflow: 'hidden',
  pointerEvents: isVisible ? 'auto' : 'none',
  transform: isVisible ? 'scale(1)' : 'scale(0.8)',
  transition: theme.transitions.create(['margin-right', 'opacity', 'transform', 'width'], {
    duration: 180,
    easing: theme.transitions.easing.easeOut,
  }),
  width: isVisible ? 'var(--secondary-navigation-scalar-owner-icon-width, 14px)' : 0,
  willChange: 'opacity, transform, width',
  '& svg': {
    display: 'block',
    height: 'var(--secondary-navigation-scalar-owner-icon-height, 15px)',
    width: 'var(--secondary-navigation-scalar-owner-icon-width, 14px)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export const SecondaryNavigationTitleAccessory = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme, zoomLevel }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  gap: getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  minWidth: 0,
}));

export const SecondaryNavigationTabsFrame = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  minWidth: 0,
  position: 'relative',
});

export const SecondaryNavigationActiveIndicator = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.navigation.secondary.tab.selectedBackground,
  bottom: 0,
  display: 'block',
  height: 'var(--secondary-navigation-tab-height, 32px)',
  left: 0,
  opacity: 'var(--active-navigation-indicator-opacity, 0)',
  pointerEvents: 'none',
  position: 'absolute',
  transform: 'translateX(var(--active-navigation-indicator-x, 0px))',
  transition: theme.transitions.create(['opacity', 'transform', 'width'], {
    duration: 220,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 'var(--active-navigation-indicator-width, 0px)',
  zIndex: 0,
}));

export const SecondaryNavigationTabs = styled(Tabs)({
  flex: '1 1 auto',
  minHeight: 'unset',
  minWidth: 0,
  position: 'relative',
  zIndex: 1,
  '& .MuiTabs-scroller': {
    overflowX: 'auto !important',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  '& .MuiTabs-flexContainer': {
    alignItems: 'center',
    minWidth: 'max-content',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

export const SecondaryNavigationSkeletonTabs = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flex: '0 1 auto',
  gap: 'var(--secondary-navigation-control-gap, 4px)',
  minWidth: 0,
  overflow: 'hidden',
});

export const SecondaryNavigationTab = styled(Tab, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme, zoomLevel }) => ({
  borderRadius: 0,
  boxSizing: 'border-box',
  color: theme.palette.navigation.secondary.tab.default,
  fontSize: getZoomAdjustedThemeTypographyPx(theme, 'caption', 'fontSize', zoomLevel),
  fontWeight: theme.typography.fontWeightMedium,
  height: getZoomAdjustedPx(32, zoomLevel),
  letterSpacing: 0,
  lineHeight: getZoomAdjustedThemeTypographyPx(theme, 'caption', 'lineHeight', zoomLevel),
  minHeight: 'unset',
  minWidth: 'unset',
  opacity: 1,
  padding: getZoomAdjustedThemeSpacingPx(theme, 's', zoomLevel),
  textTransform: 'none',
  transition: theme.transitions.create(['background-color', 'color', 'outline-color'], {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  }),
  whiteSpace: 'nowrap',
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    color: theme.palette.navigation.secondary.tab.selectedText,
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.navigation.secondary.tab.selectedText,
  },
  '&.Mui-disabled': {
    color: theme.palette.navigation.secondary.tab.disabled,
    opacity: 1,
  },
  '&:hover:not(.Mui-disabled):not(.Mui-selected)': {
    backgroundColor: theme.palette.navigation.secondary.tab.hoverBackground,
    color: theme.palette.navigation.secondary.tab.selectedText,
  },
  '&:focus-visible': {
    outline: `var(--secondary-navigation-focus-outline-width, 2px) solid ${theme.palette.navigation.secondary.tab.focusOutline}`,
    outlineOffset: 'calc(var(--secondary-navigation-focus-outline-width, 2px) * -1)',
  },
}));

export const SecondaryNavigationTabLabel = styled('span')({
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const SecondaryNavigationActions = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme, zoomLevel }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: getZoomAdjustedThemeSpacingPx(theme, 's', zoomLevel),
  justifyContent: 'flex-end',
  marginLeft: getZoomAdjustedThemeSpacingPx(theme, 's', zoomLevel),
  minWidth: 0,
  [`@media (max-width: ${SECONDARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    justifyContent: 'flex-start',
    marginLeft: 0,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    width: '100%',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
}));

export const SecondaryNavigationInfoLabelRoot = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.navigation.secondary.infoLabel.background,
  border: `var(--secondary-navigation-border-width, 1px) solid ${theme.palette.navigation.secondary.infoLabel.border}`,
  borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
  boxSizing: 'border-box',
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 'var(--secondary-navigation-font-size, 12px)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--secondary-navigation-control-gap, 4px)',
  height: 'var(--secondary-navigation-control-height, 24px)',
  justifyContent: 'center',
  letterSpacing: 0,
  lineHeight: 'var(--secondary-navigation-line-height, 16px)',
  padding: 'var(--secondary-navigation-control-gap, 4px)',
  textAlign: 'center',
  whiteSpace: 'nowrap',
}));

export const SecondaryNavigationInfoLabelText = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.navigation.secondary.infoLabel.label,
  display: 'inline-flex',
  minWidth: 0,
}));

export const SecondaryNavigationInfoLabelValue = styled('span', {
  shouldForwardProp: shouldForwardInfoLabelValueProp,
})<InfoLabelValueStyleProps>(({ theme, valueTone }) => ({
  alignItems: 'center',
  color: theme.palette.navigation.secondary.infoLabel.value[valueTone],
  display: 'inline-flex',
  minWidth: 0,
}));

export const SecondaryNavigationActionButtonRoot = styled(MuiButton, {
  shouldForwardProp: shouldForwardActionButtonProp,
})<ActionButtonStyleProps>(({ buttonState = 'idle', buttonVariant, theme }) => {
  const { actionButton } = theme.palette.navigation.secondary;
  const colors = actionButton[buttonVariant];
  const isActionState = buttonState !== 'idle';
  const isPulseVisible = buttonState === 'saving';
  const isErrorState = buttonState === 'error';
  const disabledColors = getSecondaryNavigationDisabledColors(
    theme,
    colors,
    isActionState,
    isErrorState
  );
  const variantStyles = {
    ghost: {
      backgroundColor: actionButton.ghost.background,
      borderColor: actionButton.ghost.border,
      color: actionButton.ghost.text,
      '&:hover': {
        backgroundColor: actionButton.ghost.hoverBackground,
        color: actionButton.ghost.hoverText,
      },
    },
    primary: {
      backgroundColor: actionButton.primary.background,
      borderColor: actionButton.primary.border,
      color: actionButton.primary.text,
      '&:hover': {
        backgroundColor: actionButton.primary.hoverBackground,
        borderColor: actionButton.primary.hoverBorder,
      },
    },
    positive: {
      backgroundColor: actionButton.positive.background,
      borderColor: actionButton.positive.border,
      color: actionButton.positive.text,
      '&:hover': {
        backgroundColor: actionButton.positive.hoverBackground,
        borderColor: actionButton.positive.hoverBorder,
      },
    },
    secondary: {
      backgroundColor: actionButton.secondary.background,
      borderColor: actionButton.secondary.border,
      color: actionButton.secondary.text,
      '&:hover': {
        backgroundColor: actionButton.secondary.hoverBackground,
        borderColor: actionButton.secondary.hoverBorder,
        color: actionButton.secondary.hoverText,
      },
    },
  } satisfies Record<SecondaryNavigationActionButtonVariant, object>;

  return {
    '--secondary-navigation-save-glow-color': alpha(isErrorState ? RED[400] : colors.background, 0.5),
    alignItems: 'center',
    animation: isPulseVisible ? `${secondaryNavigationSavePulse} 1200ms ease-in-out infinite` : undefined,
    borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
    borderStyle: 'solid',
    borderWidth: 'var(--secondary-navigation-border-width, 1px)',
    boxShadow: isPulseVisible ? `0 0 20px 0 ${alpha(isErrorState ? RED[400] : colors.background, 0.5)}` : 'none',
    display: 'inline-flex',
    fontSize: 'var(--secondary-navigation-font-size, 12px)',
    fontWeight: theme.typography.fontWeightMedium,
    gap: 'var(--secondary-navigation-control-gap, 4px)',
    height: 'var(--secondary-navigation-control-height, 24px)',
    justifyContent: 'center',
    letterSpacing: 0,
    lineHeight: 'var(--secondary-navigation-line-height, 16px)',
    minHeight: 'unset',
    minWidth: 'unset',
    overflow: 'hidden',
    padding:
      'var(--secondary-navigation-control-padding-y, 4px) var(--secondary-navigation-control-padding-x, 4px)',
    textTransform: 'none',
    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
      duration: 160,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&.Mui-disabled': {
      backgroundColor: disabledColors.backgroundColor,
      borderColor: disabledColors.borderColor,
      color: disabledColors.color,
      opacity: 0.65,
    },
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      color: 'inherit',
      margin: 0,
      '& svg': {
        fontSize: 'var(--secondary-navigation-action-icon-size, 16px)',
      },
    },
    ...variantStyles[buttonVariant],
    ...(isErrorState
      ? {
        backgroundColor: RED[600],
        borderColor: RED[600],
        color: theme.palette.common.white,
        '&:hover': {
          backgroundColor: RED[700],
          borderColor: RED[700],
          color: theme.palette.common.white,
        },
      }
      : {}),
  };
});

export const SecondaryNavigationActionButtonContent = styled('span')(({ theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  gap: 'var(--secondary-navigation-control-gap, 4px)',
  height: 'var(--secondary-navigation-line-height, 16px)',
  justifyContent: 'center',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative',
  transition: theme.transitions.create(['width'], {
    duration: 240,
    easing: theme.transitions.easing.easeInOut,
  }),
}));

export const SecondaryNavigationActionButtonContentItem = styled('span', {
  shouldForwardProp: prop => prop !== 'contentPosition',
})<ActionButtonContentItemStyleProps>(({ contentPosition, theme }) => {
  const contentTranslateY = (() => {
    if (contentPosition === 'active') {
      return 0;
    }

    if (contentPosition === 'next') {
      return 'calc(var(--secondary-navigation-line-height, 16px) * -1)';
    }

    return 'var(--secondary-navigation-line-height, 16px)';
  })();

  return {
    alignItems: 'center',
    display: 'inline-flex',
    gap: 'var(--secondary-navigation-control-gap, 4px)',
    justifyContent: 'center',
    left: '50%',
    lineHeight: 'var(--secondary-navigation-line-height, 16px)',
    opacity: contentPosition === 'active' ? 1 : 0,
    position: 'absolute',
    top: '50%',
    transform: `translate(-50%, -50%) translateY(${contentTranslateY})`,
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: 260,
      easing: theme.transitions.easing.easeInOut,
    }),
    whiteSpace: 'nowrap',
  };
});

export const SecondaryNavigationActionStatusIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: 'currentColor',
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 'var(--secondary-navigation-action-icon-size, 16px)',
  height: 'var(--secondary-navigation-action-icon-size, 16px)',
  justifyContent: 'center',
  opacity: 0.95,
  transformOrigin: 'center',
  transition: theme.transitions.create(['opacity', 'transform'], {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 'var(--secondary-navigation-action-icon-size, 16px)',
}));

export const SecondaryNavigationActionSavedCheck = styled('svg')({
  display: 'block',
  height: 'var(--secondary-navigation-action-icon-size, 16px)',
  overflow: 'visible',
  width: 'var(--secondary-navigation-action-icon-size, 16px)',
});

export const SecondaryNavigationActionSavedCheckPath = styled('path')({
  animation: `${secondaryNavigationSavedCheckDraw} 420ms ease-out 80ms both`,
  fill: 'none',
  stroke: 'currentColor',
  strokeDasharray: 1,
  strokeDashoffset: 1,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  strokeWidth: 2,
});

export const SecondaryNavigationIconButtonRoot = styled(MuiIconButton, {
  shouldForwardProp: shouldForwardIconButtonProp,
})<IconButtonStyleProps>(({ iconButtonVariant, isActive, theme }) => {
  const { iconButton } = theme.palette.navigation.secondary;
  const isContained = iconButtonVariant === 'contained';
  const defaultBackground = isActive ? iconButton.activeBackground : 'transparent';
  const defaultColor = isActive ? iconButton.activeText : iconButton.defaultText;
  const defaultHoverBackground = isActive ? iconButton.activeBackground : iconButton.hoverBackground;
  const iconButtonSize = isContained
    ? 'var(--secondary-navigation-contained-icon-button-size, 20px)'
    : 'var(--secondary-navigation-plain-icon-button-size, 24px)';

  return {
    backgroundColor: isContained ? iconButton.containedBackground : defaultBackground,
    borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
    color: isContained ? iconButton.containedText : defaultColor,
    height: iconButtonSize,
    padding: isContained ? 'var(--secondary-navigation-control-padding-y, 2px)' : 0,
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: 160,
      easing: theme.transitions.easing.easeInOut,
    }),
    width: iconButtonSize,
    '&:hover': {
      backgroundColor: isContained ? iconButton.containedHoverBackground : defaultHoverBackground,
      color: isContained ? iconButton.containedText : iconButton.hoverText,
    },
    '&.Mui-disabled': {
      color: iconButton.disabledText,
      opacity: 1,
    },
    '& svg': {
      fontSize: isContained
        ? 'var(--secondary-navigation-action-icon-size, 16px)'
        : 'var(--secondary-navigation-plain-icon-button-size, 24px)',
    },
  };
});

export const SecondaryNavigationIconTooltipAnchor = styled('span')({
  display: 'inline-flex',
  flex: '0 0 auto',
});

export const SecondaryNavigationSelectorRoot = styled(MuiButton, {
  shouldForwardProp: shouldForwardSelectorProp,
})<SelectorStyleProps>(({ selectorVariant, theme }) => ({
  '--secondary-navigation-selector-label-color': selectorVariant === 'filter'
    ? theme.palette.navigation.secondary.selector.filterText
    : theme.palette.navigation.secondary.selector.label,
  '--secondary-navigation-selector-value-color': selectorVariant === 'filter'
    ? theme.palette.navigation.secondary.selector.filterText
    : theme.palette.navigation.secondary.selector.value,
  '--secondary-navigation-selector-icon-color': selectorVariant === 'filter'
    ? theme.palette.navigation.secondary.selector.filterText
    : theme.palette.navigation.secondary.selector.icon,
  alignItems: 'center',
  backgroundColor: selectorVariant === 'filter'
    ? theme.palette.navigation.secondary.selector.filterBackground
    : theme.palette.navigation.secondary.selector.background,
  borderColor: selectorVariant === 'filter'
    ? 'transparent'
    : theme.palette.navigation.secondary.selector.border,
  borderRadius: 'var(--secondary-navigation-control-radius, 4px)',
  borderStyle: 'solid',
  borderWidth: 'var(--secondary-navigation-border-width, 1px)',
  boxShadow: 'none',
  color: 'var(--secondary-navigation-selector-value-color)',
  display: 'inline-flex',
  fontSize: 'var(--secondary-navigation-font-size, 12px)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--secondary-navigation-control-gap, 4px)',
  height: 'var(--secondary-navigation-control-height, 24px)',
  letterSpacing: 0,
  lineHeight: 'var(--secondary-navigation-line-height, 16px)',
  maxWidth: selectorVariant === 'filter'
    ? 'var(--secondary-navigation-filter-selector-max-width, 172px)'
    : '100%',
  minHeight: 'unset',
  minWidth: 0,
  padding:
    'var(--secondary-navigation-control-padding-y, 4px) var(--secondary-navigation-control-padding-x, 4px)',
  textTransform: 'none',
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  }),
  '&:hover': {
    '--secondary-navigation-selector-label-color': selectorVariant === 'filter'
      ? theme.palette.navigation.secondary.selector.filterText
      : theme.palette.navigation.secondary.selector.label,
    '--secondary-navigation-selector-value-color': selectorVariant === 'filter'
      ? theme.palette.navigation.secondary.selector.filterText
      : theme.palette.navigation.secondary.selector.hoverText,
    '--secondary-navigation-selector-icon-color': selectorVariant === 'filter'
      ? theme.palette.navigation.secondary.selector.filterText
      : theme.palette.navigation.secondary.selector.icon,
    backgroundColor: selectorVariant === 'filter'
      ? theme.palette.navigation.secondary.selector.filterHoverBackground
      : theme.palette.navigation.secondary.selector.hoverBackground,
    borderColor: selectorVariant === 'filter'
      ? 'transparent'
      : theme.palette.navigation.secondary.selector.hoverBorder,
    color: theme.palette.navigation.secondary.selector.hoverText,
  },
  '&.Mui-disabled': {
    '--secondary-navigation-selector-label-color': theme.palette.navigation.secondary.selector.disabledText,
    '--secondary-navigation-selector-value-color': theme.palette.navigation.secondary.selector.disabledText,
    '--secondary-navigation-selector-icon-color': theme.palette.navigation.secondary.selector.disabledText,
    backgroundColor: theme.palette.navigation.secondary.selector.disabledBackground,
    borderColor: theme.palette.navigation.secondary.selector.disabledBorder,
    color: theme.palette.navigation.secondary.selector.disabledText,
    opacity: 1,
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    color: 'var(--secondary-navigation-selector-icon-color)',
    marginLeft: 0,
    marginRight: 0,
    '& svg': {
      fontSize: 'var(--secondary-navigation-action-icon-size, 16px)',
    },
  },
  '&:focus-visible': {
    outline: `var(--secondary-navigation-focus-outline-width, 2px) solid ${alpha(theme.palette.navigation.secondary.selector.focusOutline, 0.9)}`,
    outlineOffset: 'calc(var(--secondary-navigation-focus-outline-width, 2px) * -1)',
  },
}));

const secondaryNavigationSelectorTextStyles = {
  display: 'inline-block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const SecondaryNavigationSelectorLabel = styled('span')({
  ...secondaryNavigationSelectorTextStyles,
  color: 'var(--secondary-navigation-selector-label-color)',
});

export const SecondaryNavigationSelectorValue = styled('span')({
  ...secondaryNavigationSelectorTextStyles,
  color: 'var(--secondary-navigation-selector-value-color)',
});
